import { useContext } from "react";

import { UserRegistrationForm } from "../types/userRegistrationForm";

import AuthTransactionContext from "pages/register/contexts/AuthTransactionContext";

import useCreateUser from "pages/register/hooks/useCreateUser";
import useAuthenticateUser from "pages/register/hooks/useAuthenticateUser";

import selectParams from "pages/register/util/selectParams";
import getAuthQueryParamData from "../util/getAuthQueryParamData";
import { saveRegistrationData } from "pages/register/util/registrationDataManager";
import { getEnvVariables } from "../../../lib/environment";

const useCreateAndAuthUser = () => {
  const { setTransaction } = useContext(AuthTransactionContext);
  const {
    mutate: createUser,
    isLoading: isCreatingUser,
    error: userCreationError,
  } = useCreateUser();
  const {
    mutateAsync: authenticateUserAsync,
    isLoading: isAuthenticatingUser,
    error: userAuthenticationError,
  } = useAuthenticateUser();

  const useCreateAndAuthUser = (
    formData: UserRegistrationForm,
    mfaStepEnabled: boolean,
  ) => {
    // Keep a copy instead of a reference of the formData in case the user updates an input while the request is in flight
    const userCredentials = {
      ...selectParams(formData, [
        "username",
        "firstName",
        "lastName",
        "preferredName",
        "email",
        "password",
        "groupIds",
      ]),
    };
    const authQueryParamData = getAuthQueryParamData();

    userCredentials.groupIds = [getEnvVariables().oktaCommonJwGroupId];
    if (mfaStepEnabled) {
      userCredentials.groupIds.push(getEnvVariables().oktaTestingJwGroupId);
    }

    createUser(
      {
        ...userCredentials,
        client: {
          id: authQueryParamData.clientId || "",
          redirectUri: authQueryParamData.redirectUri || "",
          state: authQueryParamData.state || "",
        },
      },
      {
        onSuccess: async () => {
          const transaction = await authenticateUserAsync({
            username: userCredentials.username,
            password: userCredentials.password,
          });

          // The user's email will not be included in any of the responses from the Okta API
          // Need to save the email locally to be used later
          saveRegistrationData({
            username: userCredentials.username,
            email: userCredentials.email,
          });
          setTransaction(transaction);
        },
      },
    );
  };

  return {
    mutate: useCreateAndAuthUser,
    isLoading: isCreatingUser || isAuthenticatingUser,
    userCreationError,
    userAuthenticationError,
  };
};

export default useCreateAndAuthUser;
